<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import {
  LAUNCHER_CLICK_ACTION_OPEN_WIDGET,
  LAUNCHER_CLICK_URL_MODE_DEFAULT,
  normalizeBubbleLauncherState,
  serializeBubbleLauncherState,
} from 'shared/helpers/bubbleLauncher';

const SCRIPT_OPEN = '\x3cscript\x3e';
const SCRIPT_CLOSE = '\x3c\x2fscript\x3e';
const DEFAULT_BUBBLE_COLOR = '#0059e1';
const DEFAULT_ICON_COLOR = '#ffffff';
const FIXED_ICON_SIZE = 32;
const BUBBLE_BG_SWATCHES = [
  '#0059e1',
  '#111827',
  '#0f172a',
  '#0284c7',
  '#059669',
  '#ea580c',
  '#dc2626',
  '#7c3aed',
];
const ICON_COLOR_SWATCHES = [
  '#ffffff',
  '#f8fafc',
  '#e2e8f0',
  '#111111',
  '#1f2937',
];

const isValidHexColor = value =>
  /^#?[0-9a-f]{6}$/i.test((value || '').toString().trim());

const normalizeHexColor = (value, fallback) => {
  const raw = (value || '').toString().trim();
  if (!isValidHexColor(raw)) return fallback;
  return raw.startsWith('#') ? raw.toLowerCase() : `#${raw.toLowerCase()}`;
};

export default {
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      state: {
        bubbleColor: DEFAULT_BUBBLE_COLOR,
        iconColor: DEFAULT_ICON_COLOR,
        iconImageUrl: '',
        launcherClickAction: LAUNCHER_CLICK_ACTION_OPEN_WIDGET,
        launcherClickUrlMode: LAUNCHER_CLICK_URL_MODE_DEFAULT,
        launcherClickOpenInNewTab: false,
        launcherClickUrl: '',
        standaloneReturnUrl: '',
      },
      pendingIconFile: null,
      originalSnapshot: '',
      isUploadOpen: true,
      isSaving: false,
      previewDocHtml: '',
    };
  },
  computed: {
    iconPreviewSize() {
      return FIXED_ICON_SIZE;
    },
    bubbleColorSwatches() {
      return BUBBLE_BG_SWATCHES;
    },
    iconColorSwatches() {
      return ICON_COLOR_SWATCHES;
    },
    normalizedBubbleColor() {
      return normalizeHexColor(this.state.bubbleColor, DEFAULT_BUBBLE_COLOR);
    },
    normalizedIconColor() {
      return normalizeHexColor(this.state.iconColor, DEFAULT_ICON_COLOR);
    },
    isDirty() {
      return this.snapshot() !== this.originalSnapshot;
    },
    saveDisabled() {
      return this.isSaving || !this.isDirty;
    },
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    resolvedPreviewDarkMode() {
      const themeConfig = this.getThemeConfig(this.inbox);
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      if (isMidnight) {
        if (themeConfig?.midnightVariant === 'light') return 'light';
        if (themeConfig?.midnightVariant === 'dark') return 'dark';
        if (themeConfig?.darkMode === 'light') return 'light';
        return 'dark';
      }
      if (['light', 'dark'].includes(themeConfig?.darkMode)) {
        return themeConfig.darkMode;
      }
      return 'light';
    },
    defaultStandaloneUrl() {
      const baseUrl = (this.livePreviewBaseUrl || '').replace(/\/$/, '');
      const token = this.livePreviewWebsiteToken || '';
      if (!baseUrl || !token) return '';
      return `${baseUrl}/widget?website_token=${encodeURIComponent(token)}`;
    },
    liveChatwootSettings() {
      const existingThemeConfig = this.getThemeConfig(this.inbox);
      const bubbleIconImageUrl = (this.state.iconImageUrl || '')
        .toString()
        .trim();
      const launcherState = serializeBubbleLauncherState(this.state);

      return {
        locale: this.inbox.widget_language || 'en',
        position: this.inbox.position || 'right',
        type: 'standard',
        allowThemeOverride: true,
        darkMode: this.resolvedPreviewDarkMode,
        themePreset: this.inbox.widget_theme_preset || 'classic',
        widgetStyle: 'rounded',
        widgetColor: this.inbox.widget_color || DEFAULT_BUBBLE_COLOR,
        iconColor: this.normalizedIconColor,
        bubbleColor: this.normalizedBubbleColor,
        bubbleIconColor: this.normalizedIconColor,
        bubbleIconImageUrl: bubbleIconImageUrl || null,
        themeConfig: {
          ...existingThemeConfig,
          bubbleColor: this.normalizedBubbleColor,
          bubbleIconColor: this.normalizedIconColor,
          bubbleIconImageUrl,
          ...launcherState,
        },
        welcomeTitle: this.inbox.welcome_title || '',
        welcomeDescription: this.inbox.welcome_tagline || '',
        announcementText: this.inbox.announcement_text || '',
        announcementUrl: this.inbox.announcement_url || '',
        announcementBgColor: this.inbox.announcement_bg_color || '#242529',
        announcementTextColor: this.inbox.announcement_text_color || '#ffffff',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        initialRoute: 'messages',
      };
    },
    previewBubbleStyles() {
      return {
        background: this.state.bubbleColor,
        color: this.state.iconColor,
      };
    },
  },
  watch: {
    inbox: {
      immediate: true,
      handler(next, prev) {
        const nextId = next?.id;
        const prevId = prev?.id;
        if (nextId && nextId !== prevId) {
          this.resetChanges();
          this.previewDocHtml = this.buildPreviewDoc();
        }
      },
    },
    state: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
  },
  mounted() {
    if (!this.previewDocHtml) {
      this.previewDocHtml = this.buildPreviewDoc();
    }
  },
  methods: {
    getThemeConfig(inbox = this.inbox) {
      const config = inbox?.widget_theme_config;
      if (!config || typeof config !== 'object' || Array.isArray(config)) {
        return {};
      }
      return config;
    },
    snapshot() {
      const launcherState = serializeBubbleLauncherState(this.state);

      return JSON.stringify({
        bubbleColor: normalizeHexColor(
          this.state.bubbleColor,
          DEFAULT_BUBBLE_COLOR
        ),
        iconColor: normalizeHexColor(this.state.iconColor, DEFAULT_ICON_COLOR),
        iconImageUrl: (this.state.iconImageUrl || '').toString().trim(),
        ...launcherState,
      });
    },
    applyInboxState(inboxData) {
      const themeConfig = this.getThemeConfig(inboxData);
      const launcherState = normalizeBubbleLauncherState(themeConfig);
      this.state = {
        bubbleColor: normalizeHexColor(
          themeConfig.bubbleColor,
          DEFAULT_BUBBLE_COLOR
        ),
        iconColor: normalizeHexColor(
          themeConfig.bubbleIconColor,
          DEFAULT_ICON_COLOR
        ),
        iconImageUrl: (themeConfig.bubbleIconImageUrl || '').toString().trim(),
        ...launcherState,
      };
      this.pendingIconFile = null;
      this.originalSnapshot = this.snapshot();
    },
    sanitizeColorField(field, fallback) {
      this.state[field] = normalizeHexColor(this.state[field], fallback);
    },
    selectBubbleColor(color) {
      this.state.bubbleColor = normalizeHexColor(color, DEFAULT_BUBBLE_COLOR);
    },
    selectIconColor(color) {
      this.state.iconColor = normalizeHexColor(color, DEFAULT_ICON_COLOR);
    },
    toggleUploadOpen() {
      this.isUploadOpen = !this.isUploadOpen;
    },
    removeUploadedIcon() {
      this.state.iconImageUrl = '';
      this.pendingIconFile = null;
    },
    onUploadFileChange(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      this.pendingIconFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.state.iconImageUrl = e.target.result;
      };
      reader.readAsDataURL(file);

      if (event?.target) event.target.value = '';
    },
    resetChanges() {
      this.applyInboxState(this.inbox);
    },
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const bg =
        this.inbox.widget_theme_preset === 'midnight' &&
        this.inbox.widget_theme_config?.background
          ? this.inbox.widget_theme_config.background
          : this.inbox.widget_theme_preset === 'midnight'
            ? this.resolvedPreviewDarkMode === 'light'
              ? '#f6f6f7'
              : '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);

      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style>` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        `.woot--bubble-holder{visibility:visible;transition:visibility 0s;}` +
        `</style>` +
        `</head><body>` +
        SCRIPT_OPEN +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{locale:(d.s&&d.s.locale)||'en',showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);};` +
        `})(document,"script");` +
        SCRIPT_CLOSE +
        `</body></html>`
      );
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe?.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (_) {
        // Ignore preview sync failures while the iframe is still booting.
      }
    },
    openWebsitePreview() {
      const url =
        this.inbox.website_url || this.inbox.channel?.website_url || '';
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async saveChanges() {
      if (this.saveDisabled || !this.inbox?.id) return;

      this.sanitizeColorField('bubbleColor', DEFAULT_BUBBLE_COLOR);
      this.sanitizeColorField('iconColor', DEFAULT_ICON_COLOR);

      this.isSaving = true;
      try {
        if (this.pendingIconFile) {
          await InboxesAPI.uploadBubbleIcon(
            this.inbox.id,
            this.pendingIconFile
          );
          await this.$store.dispatch('inboxes/get');
          const refreshed =
            this.$store.getters['inboxes/getInbox'](this.inbox.id) ||
            this.inbox;
          const refreshedConfig = this.getThemeConfig(refreshed);
          this.state.iconImageUrl = (refreshedConfig.bubbleIconImageUrl || '')
            .toString()
            .trim();
          this.pendingIconFile = null;
        }

        const existingThemeConfig = this.getThemeConfig(this.inbox);
        const launcherState = serializeBubbleLauncherState(this.state);
        const nextThemeConfig = {
          ...existingThemeConfig,
          bubbleColor: this.state.bubbleColor,
          bubbleIconColor: this.state.iconColor,
          bubbleIconImageUrl: (this.state.iconImageUrl || '').toString().trim(),
          ...launcherState,
        };

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          channel: {
            widget_theme_config: nextThemeConfig,
          },
        });

        await this.$store.dispatch('inboxes/get');
        const refreshed = this.$store.getters['inboxes/getInbox'](
          this.inbox.id
        );
        if (refreshed) {
          this.applyInboxState(refreshed);
        } else {
          this.originalSnapshot = this.snapshot();
        }

        useAlert('Bubble launcher settings updated');
      } catch (error) {
        useAlert(error?.message || 'Failed to update bubble launcher settings');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="bls-page">
    <div class="bls-layout">
      <section class="bls-config">
        <header class="bls-config__header">
          <div class="bls-config__title">Bubble launcher</div>
        </header>

        <div class="bls-config__body">
          <p class="bls-config__desc">
            Configure the floating launcher bubble separately from widget theme
            colors. Uploaded icons render at fixed {{ iconPreviewSize }}x{{
              iconPreviewSize
            }}
            px size.
          </p>
          <div class="bls-card">
            <div class="bls-card__title">Colors</div>
            <p class="bls-card__hint">
              Launcher styles only. Independent from widget theme colors.
            </p>

            <div class="bls-color-grid">
              <div class="bls-color-field">
                <label class="bls-field-label">Bubble background color</label>
                <div class="bls-color-input">
                  <input
                    v-model="state.bubbleColor"
                    class="bls-hex-input"
                    type="text"
                    spellcheck="false"
                    @blur="sanitizeColorField('bubbleColor', '#0059e1')"
                  />
                  <input
                    v-model="state.bubbleColor"
                    class="bls-color-picker"
                    type="color"
                    @input="sanitizeColorField('bubbleColor', '#0059e1')"
                  />
                </div>
                <div class="bls-swatch-row">
                  <button
                    v-for="color in bubbleColorSwatches"
                    :key="`bubble-${color}`"
                    type="button"
                    class="bls-swatch-btn"
                    :class="{
                      'bls-swatch-btn--active': normalizedBubbleColor === color,
                    }"
                    :style="{ '--bls-swatch-color': color }"
                    :aria-label="`Set bubble background color ${color}`"
                    @click="selectBubbleColor(color)"
                  />
                </div>
              </div>

              <div class="bls-color-field">
                <label class="bls-field-label">Icon color</label>
                <div class="bls-color-input">
                  <input
                    v-model="state.iconColor"
                    class="bls-hex-input"
                    type="text"
                    spellcheck="false"
                    @blur="sanitizeColorField('iconColor', '#ffffff')"
                  />
                  <input
                    v-model="state.iconColor"
                    class="bls-color-picker"
                    type="color"
                    @input="sanitizeColorField('iconColor', '#ffffff')"
                  />
                </div>
                <div class="bls-swatch-row">
                  <button
                    v-for="color in iconColorSwatches"
                    :key="`icon-${color}`"
                    type="button"
                    class="bls-swatch-btn"
                    :class="{
                      'bls-swatch-btn--active': normalizedIconColor === color,
                    }"
                    :style="{ '--bls-swatch-color': color }"
                    :aria-label="`Set icon color ${color}`"
                    @click="selectIconColor(color)"
                  />
                </div>
              </div>
            </div>

            <div class="bls-inline-preview">
              <span class="bls-inline-preview__label">Realtime preview</span>
              <button
                type="button"
                aria-label="Widget bubble quick preview"
                class="bls-inline-preview__bubble"
                :style="previewBubbleStyles"
              >
                <img
                  v-if="state.iconImageUrl"
                  class="bls-inline-preview__icon-image"
                  :src="state.iconImageUrl"
                  alt="Bubble icon"
                />
                <svg
                  v-else
                  viewBox="0 0 240 240"
                  class="bls-inline-preview__icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    :fill="state.iconColor"
                    d="M240.808 240.808H122.123C56.6994 240.808 3.45695 187.562 3.45695 122.122C3.45695 56.7031 56.6994 3.45697 122.124 3.45697C187.566 3.45697 240.808 56.7031 240.808 122.122V240.808Z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div class="bls-accordion">
            <button
              class="bls-accordion__hd"
              type="button"
              @click="toggleUploadOpen"
            >
              <span class="bls-accordion__left">
                <span class="i-lucide-upload bls-accordion__icon" />
                <span class="bls-accordion__label">Upload center icon</span>
              </span>
              <span
                class="i-lucide-chevron-right bls-accordion__chev"
                :class="{ 'bls-accordion__chev--open': isUploadOpen }"
              />
            </button>
            <div v-show="isUploadOpen" class="bls-accordion__bd">
              <label class="bls-upload">
                <input
                  class="bls-upload__input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  @change="onUploadFileChange"
                />
                <div class="bls-upload__box">
                  <img
                    v-if="state.iconImageUrl"
                    :src="state.iconImageUrl"
                    class="bls-upload__preview-img"
                    alt="Selected icon"
                  />
                  <span v-else class="i-lucide-plus-circle bls-upload__plus" />
                </div>
              </label>
              <p class="bls-upload__hint">
                png, jpg, gif, webp. Displayed at fixed
                {{ iconPreviewSize }}x{{ iconPreviewSize }} px inside the
                bubble.
              </p>
              <button
                v-if="state.iconImageUrl"
                type="button"
                class="bls-link-btn"
                @click="removeUploadedIcon"
              >
                Use default icon
              </button>
            </div>
          </div>

          <div class="bls-card">
            <div class="bls-card__title">Launcher click action</div>
            <p class="bls-card__hint">
              Choose what happens when customers click the bubble launcher.
              Eye-catcher visuals stay fully configurable, and the click action
              will follow the same launcher behavior.
            </p>

            <label class="bls-radio-row">
              <input
                v-model="state.launcherClickAction"
                type="radio"
                name="launcherClickAction"
                value="open_widget"
              />
              <span class="bls-radio-copy">
                <span class="bls-radio-title">
                  Open chat on the current page
                </span>
                <span class="bls-radio-hint">
                  Default behavior. Clicking the bubble opens the embedded
                  widget.
                </span>
              </span>
            </label>

            <label class="bls-radio-row">
              <input
                v-model="state.launcherClickAction"
                type="radio"
                name="launcherClickAction"
                value="open_url"
              />
              <span class="bls-radio-copy">
                <span class="bls-radio-title">
                  Go to standalone page or custom URL
                </span>
                <span class="bls-radio-hint">
                  Clicking the bubble navigates away from the current page
                  instead of opening the embedded widget.
                </span>
              </span>
            </label>

            <div
              v-if="state.launcherClickAction === 'open_url'"
              class="bls-launcher-link-block"
            >
              <label class="bls-radio-row bls-radio-row--nested">
                <input
                  v-model="state.launcherClickUrlMode"
                  type="radio"
                  name="launcherClickUrlMode"
                  value="default"
                />
                <span class="bls-radio-copy">
                  <span class="bls-radio-title">Default standalone page</span>
                  <span class="bls-radio-hint">
                    Use the hosted standalone widget page generated for this
                    inbox.
                  </span>
                </span>
              </label>

              <div
                v-if="state.launcherClickUrlMode === 'default'"
                class="bls-launcher-link-panel"
              >
                <input
                  class="bls-hex-input bls-readonly-input"
                  type="text"
                  :value="defaultStandaloneUrl"
                  readonly
                />
                <label class="bls-field-label">
                  Return direct link (optional)
                </label>
                <input
                  v-model="state.standaloneReturnUrl"
                  class="bls-hex-input"
                  type="text"
                  spellcheck="false"
                  placeholder="https://your-domain.com"
                />
                <p class="bls-launcher-link-hint">
                  If set, the standalone page back action will return customers
                  to this link instead of relying on the originating browser
                  page.
                </p>
              </div>

              <label class="bls-radio-row bls-radio-row--nested">
                <input
                  v-model="state.launcherClickUrlMode"
                  type="radio"
                  name="launcherClickUrlMode"
                  value="custom"
                />
                <span class="bls-radio-copy">
                  <span class="bls-radio-title">Custom URL override</span>
                  <span class="bls-radio-hint">
                    Send customers to your own destination instead of the
                    default standalone page.
                  </span>
                </span>
              </label>

              <div
                v-if="state.launcherClickUrlMode === 'custom'"
                class="bls-launcher-link-panel"
              >
                <input
                  v-model="state.launcherClickUrl"
                  class="bls-hex-input"
                  type="text"
                  spellcheck="false"
                  placeholder="https://your-domain.com/livechat"
                />
                <label class="bls-checkbox-row">
                  <input
                    v-model="state.launcherClickOpenInNewTab"
                    type="checkbox"
                  />
                  <span>Open in new tab</span>
                </label>
                <p class="bls-launcher-link-hint">
                  If the custom URL is empty or invalid, it will fall back to
                  the default standalone page.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="bls-save-bar">
          <button
            class="bls-btn bls-btn--ghost"
            type="button"
            @click="resetChanges"
          >
            Cancel
          </button>
          <button
            class="bls-btn bls-btn--primary"
            type="button"
            :disabled="saveDisabled"
            @click="saveChanges"
          >
            {{ isSaving ? 'Saving...' : 'Save changes' }}
          </button>
        </div>
      </section>

      <section class="bls-preview-panel">
        <div class="bls-preview-header-bar">
          <div class="bls-preview-title">Preview</div>
        </div>
        <div class="bls-preview-content-area">
          <div class="bls-preview-scroll-inner">
            <div class="bls-preview-widget-holder">
              <div class="bls-preview-widget-stage">
                <div class="bls-preview-widget-inner">
                  <iframe
                    v-if="previewDocHtml"
                    ref="sdkFrame"
                    class="bls-preview-iframe"
                    :srcdoc="previewDocHtml"
                    title="Bubble launcher preview"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  />
                  <div v-else class="bls-preview-empty">
                    Widget preview is unavailable for this inbox.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.bls-page {
  --bls-accent: #0059e1;
  --bls-surface: #ffffff;
  --bls-surface2: #f6f6f7;
  --bls-border: #dde2e6;
  --bls-border-light: #eeeeef;
  --bls-text: #111827;
  --bls-text-muted: #6b7280;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: var(--bls-surface);
  display: flex;
  flex-direction: column;
}
.bls-layout {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}
.bls-config {
  flex: 5 1 62.5%;
  min-width: 296px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid #eeeeef;
}
.bls-config__header {
  flex-shrink: 0;
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #eeeeef;
}
.bls-config__title {
  margin: 0;
  height: 56px;
  line-height: 56px;
  padding-left: 21px;
  letter-spacing: 0.3px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}
.bls-config__body {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 32px 32px 120px;
}
.bls-config__desc {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
  max-width: 760px;
}
.bls-save-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  background: var(--bls-surface);
}
.bls-card {
  border: 1px solid var(--bls-border);
  border-radius: 4px;
  padding: 16px;
  background: var(--bls-surface);
  margin-bottom: 12px;
  max-width: 705px;
}
.bls-card__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--bls-text);
}
.bls-card__hint {
  margin: 8px 0 14px;
  font-size: 13px;
  line-height: 20px;
  color: var(--bls-text-muted);
}
.bls-radio-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid var(--bls-border-light);
  cursor: pointer;
}
.bls-radio-row--nested {
  padding-bottom: 8px;
}
.bls-radio-row:first-of-type {
  border-top: none;
  padding-top: 0;
}
.bls-radio-row input {
  margin-top: 2px;
}
.bls-radio-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.bls-radio-title {
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--bls-text);
}
.bls-radio-hint {
  font-size: 12px;
  line-height: 18px;
  color: var(--bls-text-muted);
}
.bls-launcher-link-block {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--bls-border-light);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bls-launcher-link-panel {
  padding: 0 0 4px 26px;
}
.bls-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 13px;
  color: var(--bls-text);
  cursor: pointer;
}
.bls-checkbox-row input {
  margin: 0;
}
.bls-readonly-input {
  color: #6b7280;
  background: #f8fafc;
}
.bls-launcher-link-hint {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--bls-text-muted);
}
.bls-color-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.bls-color-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bls-field-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--bls-text);
  line-height: 20px;
}
.bls-color-input {
  display: flex;
  gap: 8px;
  align-items: center;
}
.bls-hex-input {
  flex: 1;
  min-width: 0;
  height: 38px;
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 13px;
  outline: none;
  color: #111827;
  background: #fff;
  box-sizing: border-box;
}
.bls-hex-input:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.12);
}
.bls-color-picker {
  width: 42px;
  height: 38px;
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  padding: 2px;
}
.bls-swatch-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}
.bls-swatch-btn {
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  border: 1px solid #bcc6d0;
  background: var(--bls-swatch-color);
  cursor: pointer;
  padding: 0;
}
.bls-swatch-btn--active {
  box-shadow: 0 0 0 2px #0059e1;
  border-color: transparent;
}
.bls-inline-preview {
  margin-top: 12px;
  border-top: 1px solid var(--bls-border-light);
  padding-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.bls-inline-preview__label {
  font-size: 13px;
  color: var(--bls-text-muted);
  font-weight: 600;
}
.bls-inline-preview__bubble {
  width: 52px;
  height: 52px;
  border-radius: 9999px;
  border: none;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  padding: 0;
}
.bls-inline-preview__icon-svg,
.bls-inline-preview__icon-image {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}
.bls-accordion {
  border: 1px solid var(--bls-border);
  border-radius: 4px;
  background: var(--bls-surface);
  margin-bottom: 12px;
  overflow: hidden;
  max-width: 705px;
}
.bls-accordion__hd {
  width: 100%;
  min-height: 60px;
  background: var(--bls-surface);
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  cursor: pointer;
}
.bls-accordion__hd:hover {
  background: rgba(0, 0, 0, 0.03);
}
.bls-accordion__left {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}
.bls-accordion__icon {
  font-size: 18px;
  color: #111827;
  width: 18px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.bls-accordion__label {
  font-size: 16px;
  font-weight: 600;
  color: var(--bls-text);
}
.bls-accordion__chev {
  color: #111827;
  font-size: 16px;
  transition: transform 150ms ease;
}
.bls-accordion__chev--open {
  transform: rotate(90deg);
}
.bls-accordion__bd {
  padding: 16px;
  border-top: 1px solid var(--bls-border-light);
  background: #fff;
}
.bls-upload__input {
  display: none;
}
.bls-upload__box {
  width: 100%;
  height: 164px;
  border-radius: 8px;
  border: 2px dashed #bcc6d0;
  background: #f8fafc;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: border-color 150ms ease;
}
.bls-upload__box:hover {
  border-color: var(--bls-accent);
}
.bls-upload__plus {
  font-size: 44px;
  color: var(--bls-accent);
}
.bls-upload__preview-img {
  max-width: 80px;
  max-height: 80px;
  object-fit: contain;
}
.bls-upload__hint {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--bls-text-muted);
}
.bls-link-btn {
  margin-top: 10px;
  border: none;
  background: transparent;
  color: #0059e1;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.bls-link-btn:hover {
  text-decoration: underline;
}
.bls-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
}
.bls-btn--ghost {
  background: var(--bls-surface);
  border-color: #bcc6d0;
  color: #374151;
}
.bls-btn--ghost:hover {
  background: #f5f5f5;
}
.bls-btn--primary {
  background: #0059e1;
  border-color: #0059e1;
  color: #fff;
}
.bls-btn--primary:hover:enabled {
  background: #0047c2;
  border-color: #0047c2;
}
.bls-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.bls-preview-panel {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.bls-preview-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.bls-preview-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  margin-left: 20px;
}

.bls-preview-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;
}

.bls-preview-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.bls-preview-widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.bls-preview-widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.bls-preview-widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

.bls-preview-iframe {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.bls-preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

.bls-preview-test-note {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 8px 0 16px;
}

.bls-preview-test-note a {
  color: #0059e1;
  text-decoration: none;
}

.bls-preview-test-note a:hover {
  text-decoration: underline;
}
@media (max-width: 1240px) {
  .bls-layout {
    flex-direction: column;
  }
  .bls-config {
    flex: 1 1 auto;
    border-right: none;
    border-bottom: 1px solid var(--bls-border);
  }
  .bls-preview-panel {
    border-left: none;
    flex: 1 1 auto;
  }
}
@media (max-width: 768px) {
  .bls-config__body {
    padding: 16px;
  }
  .bls-save-bar {
    padding: 12px 16px;
  }
  .bls-color-grid {
    grid-template-columns: 1fr;
  }
}
</style>
